import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapActions } from "pinia";

import { useApp } from "../../../pages-vue/App/useApp";
import { MessageService } from "../../../services/message-service";
import { UserService } from "../../../services/user-service";
import { AgencyService, IGetOrganization } from "../../../services/agency.service";
import { ApplicationEnum, PatientStatus, PatientStatusEnum, SelectProfileEnum } from "../../../enums/enums";
import { getApplicationId } from "../../../common/vue-helpers/modifiers/application-modifiers";
import { debounce } from "../../../common/vue-helpers/debounce";
import { capitalizeFirstLetter } from "../../../utilities/capitalizeFirstLetter";
import { IPatientList } from "../../vue-interfaces/messaging-service/i-patient";
import { IGetUsersForProfileQuery, IUsersProfileList } from "../../vue-interfaces/messaging-service/i-user";

import ScrollPagination from "../../../resources-vue/vue-elements/ScrollPagination/ScrollPagination.vue";
import ProductPill from "../../vue-elements/ProductPill/ProductPill.vue";

export default defineComponent({
    inject: ["dialogRef"],
    components: {
        ScrollPagination,
        ProductPill
    },
    watch: {
        search: function (newValue, oldValue) {
            if (newValue != oldValue) {
                this.searchTermDebounce();
            }
        },
        selectedOptions() {
            this.$nextTick(() => {
                const container = this.$refs.selectedNamesScroll as HTMLElement;
                if (container) {
                    container.scrollTo({
                        left: container.scrollWidth,
                        behavior: "smooth"
                    });
                }
            });
        }
    },
    data() {
        return {
            search: "" as string,
            searchTermDebounce: null,
            open: false as boolean,
            dialogReference: this.dialogRef as any,
            lists: [] as IPatientList[] | IUsersProfileList[],
            dialogData: null as any,
            profileType: "" as string,
            conversationId: "" as string,
            userOs: "",
            selectedOptions: [],
            _messageService: null as MessageService,
            _userService: null as UserService,
            _agencyService: null,
            organizations: [] as IGetOrganization[],
            agencyIds: [] as string[],
            isLoading: true,
            pageNumber: 1 as number,
            pageLength: 25 as number,
            pageCount: null as number,
            hasError: false,
            page: null,
            loadMore: false as boolean,
            PatientStatus,
            PatientStatusEnum,
            SelectProfileEnum,
            getApplicationId,
            capitalizeFirstLetter
        };
    },
    created() {
        this._messageService = Container.instance.get(MessageService);
        this._userService = Container.instance.get(UserService);
        this.userOs = window.sessionStorage.getItem("userOs");
        this._agencyService = Container.instance.get(AgencyService);
        this.searchTermDebounce = debounce(this.searchUpdated, 500);
    },
    async mounted() {
        this.dialogData = this.dialogReference.data;
        this.profileType = this.dialogData.name;
        this.conversationId = this.dialogData.conversationId;

        const targetApplicationIds = new Set([
            ApplicationEnum.AgencyCore,
            ApplicationEnum.HomeCare,
            ApplicationEnum.AxxessHospice,
            ApplicationEnum.AxxessHospiceFE,
            ApplicationEnum.AxxessPalliative
        ]);
        this.agencyIds = this.getSolutionAgencyIds(targetApplicationIds);
        this.searchUpdated();
    },

    computed: {
        getSearchPlaceholder(): string {
            return `Search for ${capitalizeFirstLetter(this.profileType)}`;
        },
        allPagesLoaded(): boolean {
            return !!this.pageCount && this.pageNumber > this.pageCount;
        }
    },
    methods: {
        ...mapActions(useApp, {
            getSolutionAgencyIds: "getSolutionAgencyIds"
        }),

        cancel() {
            this.dialogReference.close("cancel");
        },

        async getUsersList(filter: string, isPaginated: boolean = false) {
            try {
                if (isPaginated) this.loadMore = true;
                else this.isLoading = true;

                this.hasError = false;

                const query: IGetUsersForProfileQuery = {
                    page: this.pageNumber,
                    pageLength: this.pageLength,
                    term: filter.trim(),
                    conversationId: this.conversationId
                };
                const data = await this._messageService.getUsersForProfile(query);
                if (data?.items?.length > 0) {
                    this.page = data;
                    this.pageCount = data.pageCount;
                    this.pageNumber = data.currentPage;

                    this.lists = isPaginated
                        ? ((this.lists as IUsersProfileList[]).concat(data.items) as IUsersProfileList[])
                        : data.items;

                    return data.items;
                }

                if (!isPaginated) this.lists = [];

                return [];
            } catch (error) {
                console.error(error);
                this.hasError = true;
                return [];
            } finally {
                if (isPaginated) this.loadMore = false;
                else this.isLoading = false;
            }
        },

        async fetchPatientsList(filter: string = "", isPaginated: boolean = false) {
            try {
                if (isPaginated) this.loadMore = true;
                else this.isLoading = true;

                this.hasError = false;

                const data = await this._messageService.getPatientsByName({
                    term: filter.trim(),
                    page: this.pageNumber,
                    pageLength: this.pageLength,
                    conversationId: this.conversationId
                });

                if (data?.items?.length > 0) {
                    this.page = data;
                    this.pageCount = data.pageCount;
                    this.pageNumber = data.currentPage;

                    this.lists = isPaginated
                        ? ((this.lists as IPatientList[]).concat(data.items) as IPatientList[])
                        : data.items;

                    return data.items;
                }

                if (!isPaginated) this.lists = [];

                return [];
            } catch (error) {
                console.error(error);
                this.hasError = true;
                return [];
            } finally {
                if (isPaginated) this.loadMore = false;
                else this.isLoading = false;
            }
        },

        searchUpdated() {
            this.pageNumber = 1;
            if (this.profileType == SelectProfileEnum.Employee) {
                let results = this.getUsersList(this.search);
                results.then((users: IUsersProfileList[]) => {
                    this.lists = users;
                });
            } else {
                let results = this.fetchPatientsList(this.search);
                results.then((users: IPatientList[]) => {
                    this.lists = users;
                });
            }
        },

        updateSelectedItems() {
            this.dialogReference.close(this.selectedOptions);
        },

        pageNumberUpdated(pageToLoad: number) {
            if (!pageToLoad) {
                return;
            }
            if (pageToLoad != this.pageNumber) {
                this.pageNumber = pageToLoad;
            } else {
                return;
            }
            if (!this.allPagesLoaded) {
                if (this.profileType == SelectProfileEnum.Employee) {
                    this.getUsersList(this.search, true);
                } else if (this.profileType == SelectProfileEnum.Patient) {
                    this.fetchPatientsList(this.search, true);
                }
            }
        }
    }
});
